import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./manage-users.scss";
import AddEditUserModal from "../../components/add-edit-user-modal/add-edit-user-modal";
import DeleteUserConfirmationModal from "../../components/delete-user-confirmation-modal/delete-user-confirmation-modal";
import ManageMemberUtils from "../../utils/manage-member-utils";
import Button from "../../../../shared/components/dashboard-layout/components/button/buttonComponent";
import Table from "../../../../shared/components/dashboard-layout/components/table/tableComponent";
import OrganizationApi from "../../../../api/organization-api";

// Backend role values are kept as-is; this map controls only the display label.
const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  user: "User",
  b2c: "Co-ordinator",
  bot: "Bot",
};

const ManageUsersPage = () => {
  const { userInfo } = useSelector((state) => state.userStore);

  const [users, setUsers] = useState([]);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isB2COrg, setIsB2COrg] = useState(false);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!organizationId) return;
      try {
        const response = await OrganizationApi.private.fetchOrganization(organizationId);
        const org = response?.data?.data;
        if (org?.featureFlags?.b2c && org?.featureFlags?.isPrimaryB2C) {
          setIsB2COrg(true);
        }
      } catch (err) {
        console.error("Error fetching organization details:", err);
      }
    };
    fetchOrgDetails();
  }, [organizationId]);

  const refreshUsers = async () => {
    const filters = {};
    if (searchTerm) {
      filters.search = searchTerm;
    }
    if (roleFilter) {
      filters.role = roleFilter;
    }
    
    const paginationOptions = {
      page: currentPage,
      limit: limit,
      sort: `${sortOrder === "desc" ? "-" : ""}${sortField}`,
    };

    const response = await ManageMemberUtils.fetchAllMembers(filters, paginationOptions);
    setUsers(response.docs || []);
    setTotalPages(response.totalPages || 1);
    setTotalDocs(response.totalDocs || 0);
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      refreshUsers();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(debounceTimer);
  }, [currentPage, limit, sortField, sortOrder, searchTerm, roleFilter]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowAddEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (selectedUser) {
      const updatedUser = { ...selectedUser, status: "deleted" };
      await ManageMemberUtils.updateMember(updatedUser);
      refreshUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const toggleUserStatus = async (user) => {
    const updatedUser = {
      ...user,
      status: user.status === "active" ? "inactive" : "active",
    };
    await ManageMemberUtils.updateMember(updatedUser);
    refreshUsers();
  };

  const handleCloseAddEditModal = () => {
    setShowAddEditModal(false);
    setSelectedUser(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const tableColumns = React.useMemo(
    () => [
      {
        key: "email",
        header: "Email",
        cell: (user) => user.email,
        sortable: true,
      },
      {
        key: "name",
        header: "Name",
        cell: (user) => user.name || "--",
        sortable: true,
      },
      {
        key: "role",
        header: "Role",
        cell: (user) => ROLE_LABELS[user.role] || user.role,
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        cell: (user) => (
          <span
            className={`badge ${user.status === "active" ? "badge-soft-success" : "badge-soft-danger"}`}
          >
            {(user.status || "").charAt(0).toUpperCase() + (user.status || "").slice(1)}
          </span>
        ),
        sortable: true,
      },
      {
        key: "phone",
        header: "Contact No",
        cell: (user) => user.phone || "--",
        sortable: true,
      },
      {
        key: "actions",
        header: "Edit / Deactivate / Delete",
        cell: (user) =>
          user.email !== userInfo.email ? (
            <>
              <button
                className="bouncy-button white small me-1"
                onClick={() => handleEditUser(user)}
                type="button"
              >
                <i className="mdi mdi-pencil"></i>
              </button>
              <button
                className="bouncy-button white small me-1"
                onClick={() => toggleUserStatus(user)}
                type="button"
              >
                {user.status === "active" ? (
                  <i className="mdi mdi-account-off"></i>
                ) : (
                  <i className="mdi mdi-account-check"></i>
                )}{" "}
              </button>
              <button
                className="bouncy-button negative small"
                onClick={() => handleDeleteUser(user)}
                type="button"
              >
                <i className="mdi mdi-delete"></i>
              </button>
            </>
          ) : null,
      },
    ],
    [userInfo.email, handleEditUser, toggleUserStatus, handleDeleteUser]
  );

  return (
    <div className="manage-users-page">
      <div className="container-fluid">{/* Page-Title */}</div>
      <div className="row">
        <div className="col-12">
          <div className="pageHeaderTitle">
            <div className="pageTitle">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
              User Management
            </div>

            <div className="right">
              <Button
                className={"small"}
                onClick={() => {
                  setSelectedUser(null);
                  setShowAddEditModal(true);
                }}
              >
                <i className="fas fa-plus"></i> <span>Add New User</span>
              </Button>
            </div>
          </div>

          <div className="search-filter-section" style={{ marginBottom: "20px" }}>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or phone..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <select
                    className="form-select"
                    value={roleFilter}
                    onChange={handleRoleFilterChange}
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="user">User</option>
                    <option value="b2c">Co-ordinator</option>
                    <option value="bot">Bot</option>
                  </select>
                </div>
              </div>
              <div className="col-md-5 text-end">
                <span className="text-muted">
                  Showing {users.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
                  {Math.min(currentPage * limit, totalDocs)} of {totalDocs} users
                </span>
              </div>
            </div>
          </div>

          <Table 
            columns={tableColumns} 
            data={users} 
            rowKey="_id"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
          />

          <div className="pagination-section" style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <select
                className="form-select"
                style={{ width: "auto", display: "inline-block" }}
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div>
              <button
                className="bouncy-button white small me-1"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                type="button"
              >
                <i className="mdi mdi-chevron-double-left"></i>
              </button>
              <button
                className="bouncy-button white small me-1"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                type="button"
              >
                <i className="mdi mdi-chevron-left"></i>
              </button>
              <span className="mx-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="bouncy-button white small me-1"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                type="button"
              >
                <i className="mdi mdi-chevron-right"></i>
              </button>
              <button
                className="bouncy-button white small"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                type="button"
              >
                <i className="mdi mdi-chevron-double-right"></i>
              </button>
            </div>
          </div>

          {/* <div className="">
            
            <div className="tableWrapper mt-3">
              <div className="table-responsive">
                <table className="table">
                  <thead className="thead-light">
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Contact No</th>
                      <th>Edit / Deactivate / Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>{user.email}</td>
                        <td>{user.name || "--"}</td>
                        <td>{user.role}</td>
                        <td>
                          <span
                            className={`badge ${
                              user.status === "active"
                                ? "badge-soft-success"
                                : "badge-soft-danger"
                            }`}
                          >
                            {user.status.charAt(0).toUpperCase() +
                              user.status.slice(1)}
                          </span>
                        </td>
                        <td>{user.phone || "--"}</td>
                        <td>
                          {user.email !== userInfo.email && (
                            <>
                              <button
                                className="bouncy-button white small me-1"
                                onClick={() => handleEditUser(user)}
                              >
                                <i className="mdi mdi-pencil"></i>
                              </button>
                              <button
                                className="bouncy-button white small me-1"
                                onClick={() => toggleUserStatus(user)}
                              >
                                {user.status === "active" ? <i className="mdi mdi-account-off"></i> : <i className="mdi mdi-account-check"></i>}{" "}
                              </button>
                              <button
                                className="bouncy-button negative small"
                                onClick={() => handleDeleteUser(user)}
                              >
                                <i className="mdi mdi-delete"></i>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div> */}
        </div>
      </div>
      <AddEditUserModal
        show={showAddEditModal}
        onClose={handleCloseAddEditModal}
        user={selectedUser}
        onUserUpdated={refreshUsers}
        isB2COrg={isB2COrg}
      />
      <DeleteUserConfirmationModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={confirmDeleteUser}
        userName={selectedUser?.name}
      />
    </div>
  );
};

export default ManageUsersPage;
